import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Search, Upload, X } from 'lucide-react';
import { isUnsubscribeSuppressionReason } from '@evi/shared';
import {
  listLeads, updateLead, touchLead, fetchLeadIntelligence, suppressLead, unsuppressLead,
  leadFilterQuery, LEAD_AUDIENCE_LISTS, LEAD_AUDIENCE_LIST_LABELS, getDisplayErrorMessage,
  type LeadRow, type LeadListParams, type LeadAudienceList,
} from '@/lib/api';
import { unsuppressReconsentPrompt, unsuppressConfirmText } from '@/lib/leadSuppression';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { ImportLeadsModal } from './ImportLeadsModal';
import styles from './LeadsPage.module.css';

/**
 * Admin Leads — the CRM view over the unified Lead collection. List + filter,
 * inline status change (audited server-side), CSV export of the current
 * filter (Jason's download), and a detail drawer with notes editing.
 *
 * Nav-gated to super_admin / operator; the server routes 403 anyone else, so
 * a hand-typed URL degrades to an empty error state.
 */

const AUDIENCES = [
  'driver_prospect', 'investor', 'newsletter', 'sales_partner',
  'operator_prospect', 'social', 'other',
] as const;
const STATUSES = ['new', 'contacted', 'qualified', 'customer', 'unsubscribed'] as const;
const BANDS = ['hot', 'warm', 'nurture', 'dormant'] as const;
const PAGE_SIZE = 50;

/** Band chip class per band — token colors (hot=warn, warm=accent,
 *  nurture=info, dormant=mute). */
const BAND_CLASS: Record<string, string> = {
  hot: styles.bandHot!,
  warm: styles.bandWarm!,
  nurture: styles.bandNurture!,
  dormant: styles.bandDormant!,
};

function humanize(s: string | null | undefined): string {
  // Guard undefined/null: a legacy lead row can lack audience/status (created
  // before the model extension, backfill not yet run), and calling .replace on
  // undefined is what crashed the list. Return '' so it never throws; call
  // sites pass 'other'/'new' fallbacks for a meaningful label.
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
/** An evinitiative.com address, subdomains included: always internal (the Api's
 *  lib/leads/internal.ts isEviDomainEmail), so the drawer cannot turn it off. */
function isEviDomainEmail(email: string): boolean {
  return /@(?:[^@\s]+\.)?evinitiative\.com$/i.test(email.trim());
}
/** Why a lead is internal, in words. */
const INTERNAL_REASON_TEXT: Record<string, string> = {
  evi_domain: 'Every evinitiative.com address is internal.',
  staff_list: 'This address is on the staff test list in Settings. Turning it off here also takes it off that list.',
  manual: 'Marked by hand in this drawer.',
};

/** ISO timestamp → local YYYY-MM-DD for a <input type="date"> value. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface Props {
  onNavigate?: (to: string) => void;
  /** Seed the audience filter and rename the surface.
   *
   *  EVI › Partners › Applications mounts this page with
   *  'sales_partner' to serve the "See if I qualify" review queue.
   *  There is no separate applications model: a qualification
   *  submission is captured by POST /public/leads with source
   *  'sp_qualification', which audienceForSource maps to the
   *  sales_partner audience (apps/api/src/lib/leadAudience.ts) and
   *  leadEmails notifies lead-ops about. So the queue IS this page, and
   *  the honest way to give it a home is a seeded filter behind its own
   *  address — not a second leaf pointing at the same unfiltered view.
   *
   *  The filter stays editable: it is a starting point, not a lock. */
  initialAudience?: string;
  /** Page title override, so the Applications address does not present
   *  itself as the whole Leads CRM. */
  title?: string;
}

export function LeadsPage(props: Props) {
  useRecentRoute(props.title ?? 'Leads');
  const qc = useQueryClient();

  const [audience, setAudience] = useState(props.initialAudience ?? '');
  const [lists, setLists] = useState<LeadAudienceList[]>([]);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [tag, setTag] = useState('');
  const [band, setBand] = useState('');
  const [q, setQ] = useState('');
  const [overdue, setOverdue] = useState(false);
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<LeadRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const toggleList = (l: LeadAudienceList) => {
    setLists((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));
    setPage(1);
  };

  const params: LeadListParams = useMemo(() => ({
    audience: audience || undefined,
    list: lists.length ? lists.join(',') : undefined,
    status: status || undefined,
    source: source.trim() || undefined,
    tag: tag.trim() || undefined,
    band: band || undefined,
    q: q.trim() || undefined,
    overdue: overdue || undefined,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    page,
    limit: PAGE_SIZE,
  }), [audience, lists, status, source, tag, band, q, overdue, createdFrom, createdTo, page]);

  const leadsQ = useQuery({
    queryKey: ['admin-leads', params],
    queryFn: () => listLeads(params),
    staleTime: 15_000,
  });

  const statusMut = useMutation({
    mutationFn: (v: { id: string; status: string }) => updateLead(v.id, { status: v.status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-leads'] }); },
  });

  const rows = leadsQ.data?.leads ?? [];
  const total = leadsQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive = !!(
    audience || lists.length || status || source || tag || band || q || overdue
    || createdFrom || createdTo
  );

  const resetFilters = () => {
    // Back to THIS surface's starting point, not to the whole CRM. On
    // the Applications address the seeded audience is what makes the
    // page that queue, so clearing filters must not quietly turn it
    // into an unfiltered lead list under an "Applications" heading.
    setAudience(props.initialAudience ?? '');
    setLists([]); setStatus(''); setSource(''); setTag(''); setBand(''); setQ('');
    setOverdue(false); setCreatedFrom(''); setCreatedTo(''); setPage(1);
  };

  const onExport = async () => {
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      // Export honors the FULL current filter (previously dropped band, overdue,
      // and — new in slice 3 — list membership + the date range). leadFilterQuery
      // is the single source of truth shared with the list request.
      await downloadAuthenticatedCsv({
        path: 'admin/leads/export',
        query: leadFilterQuery(params),
        fallbackFilename: `leads-${today}.csv`,
      });
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{props.title ?? 'Leads'}</h1>
          <p className={styles.subtitle}>
            {props.initialAudience === 'sales_partner'
              ? 'Sales Partner qualification submissions, newest first. Reviewed within one business day.'
              : 'Every contact across the platform. Mongo is the system of record.'}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryBtn} onClick={() => setImportOpen(true)}>
            <Upload size={14} /> Import
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={onExport} disabled={csvBusy}>
            <Download size={14} /> {csvBusy ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </header>

      {csvError && <div className={styles.errorBanner}>{csvError}</div>}

      <div className={styles.filters}>
        <div className={styles.searchBar}>
          <Search size={14} className={styles.searchIcon} aria-hidden />
          <input
            className={styles.searchInput}
            placeholder="Search email or name"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            aria-label="Search leads"
          />
        </div>
        <select className={styles.select} value={audience} onChange={(e) => { setAudience(e.target.value); setPage(1); }} aria-label="Audience filter">
          <option value="">All audiences</option>
          {AUDIENCES.map((a) => <option key={a} value={a}>{humanize(a)}</option>)}
        </select>
        <select className={styles.select} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        <input className={styles.textFilter} placeholder="Source" value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} aria-label="Source filter" />
        <input className={styles.textFilter} placeholder="Tag" value={tag} onChange={(e) => { setTag(e.target.value); setPage(1); }} aria-label="Tag filter" />
        <select className={styles.select} value={band} onChange={(e) => { setBand(e.target.value); setPage(1); }} aria-label="Band filter">
          <option value="">All bands</option>
          {BANDS.map((b) => <option key={b} value={b}>{humanize(b)}</option>)}
        </select>
        <button
          type="button"
          className={overdue ? `${styles.toggleBtn} ${styles.toggleBtnActive}` : styles.toggleBtn}
          aria-pressed={overdue}
          onClick={() => { setOverdue((v) => !v); setPage(1); }}
          title="Hot or warm leads past their next action date"
        >
          Overdue
        </button>
        {filtersActive && <button type="button" className={styles.clearBtn} onClick={resetFilters}>Clear</button>}
      </div>

      {/* Slice 3: send-list membership chips + created-at range. The list
          chips drive the export "pull the investors list" case. */}
      <div className={styles.filters}>
        <span className={styles.filterGroupLabel}>Lists</span>
        {LEAD_AUDIENCE_LISTS.map((l) => (
          <button
            key={l}
            type="button"
            className={lists.includes(l) ? `${styles.toggleBtn} ${styles.toggleBtnActive}` : styles.toggleBtn}
            aria-pressed={lists.includes(l)}
            onClick={() => toggleList(l)}
          >
            {LEAD_AUDIENCE_LIST_LABELS[l]}
          </button>
        ))}
        <span className={styles.filterGroupLabel}>Created</span>
        <input
          type="date"
          className={styles.textFilter}
          value={createdFrom}
          onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }}
          aria-label="Created from"
        />
        <span className={styles.muted}>–</span>
        <input
          type="date"
          className={styles.textFilter}
          value={createdTo}
          onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }}
          aria-label="Created to"
        />
      </div>

      <p className={styles.countLine}>
        {leadsQ.isLoading ? 'Loading…' : `${total.toLocaleString()} lead${total === 1 ? '' : 's'}`}
      </p>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Email</th><th>Name</th><th>Band</th><th>Audience</th><th>Source</th>
              <th>Status</th><th>Consent</th><th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className={styles.row} onClick={() => setSelected(l)}>
                <td className={styles.emailCell}>
                  {l.email}
                  {l.suppressed && <span className={styles.suppressedBadge}>suppressed</span>}
                  {l.unsubscribed_at && <span className={styles.unsubBadge}>unsub</span>}
                  {l.internal && <span className={styles.internalBadge} title="Internal or test lead: never scored, digested or sent a campaign">Internal</span>}
                </td>
                <td>{l.name ?? <span className={styles.muted}>—</span>}</td>
                <td>
                  <span className={`${styles.bandChip} ${BAND_CLASS[l.band ?? 'dormant'] ?? styles.bandDormant}`}>
                    {humanize(l.band ?? 'dormant')}{l.lead_score ? ` ${l.lead_score}` : ''}
                  </span>
                </td>
                <td>{humanize(l.audience ?? 'other')}</td>
                <td><code className={styles.codeCell}>{l.source}</code></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <select
                    className={styles.statusSelect}
                    value={l.status ?? 'new'}
                    onChange={(e) => statusMut.mutate({ id: l.id, status: e.target.value })}
                    aria-label={`Status for ${l.email}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
                  </select>
                </td>
                <td className={styles.muted}>{fmtDate(l.consent_at)}</td>
                <td className={styles.muted}>{fmtDate(l.created_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && !leadsQ.isLoading && (
              <tr>
                <td colSpan={8} className={styles.emptyCell}>
                  {filtersActive ? 'No leads match your filters.' : 'No leads yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <button type="button" className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
        <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
        <button type="button" className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setSelected(updated);
            void qc.invalidateQueries({ queryKey: ['admin-leads'] });
          }}
        />
      )}

      {importOpen && (
        <ImportLeadsModal
          onClose={() => setImportOpen(false)}
          onImported={() => { void qc.invalidateQueries({ queryKey: ['admin-leads'] }); }}
        />
      )}
    </div>
  );
}

function LeadDrawer({
  lead, onClose, onSaved,
}: {
  lead: LeadRow;
  onClose: () => void;
  onSaved: (l: LeadRow) => void;
}) {
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = notes !== (lead.notes ?? '');
  // Follow-up scheduling + touch (slice 2).
  const [nextAction, setNextAction] = useState(toDateInput(lead.next_action_at));
  const [savingNext, setSavingNext] = useState(false);
  const [touching, setTouching] = useState(false);
  const nextDirty = nextAction !== toDateInput(lead.next_action_at);
  // Send-list memberships + global suppression (slice 3).
  const [aud, setAud] = useState<LeadAudienceList[]>(
    (lead.audiences ?? []) as LeadAudienceList[],
  );
  const [savingAud, setSavingAud] = useState(false);
  const [suppressing, setSuppressing] = useState(false);
  // Internal / test (28 October 2026): holds for the address. An
  // evinitiative.com address that is already marked stays on.
  const [markingInternal, setMarkingInternal] = useState(false);
  const eviDomain = isEviDomainEmail(lead.email);
  const internalLocked = eviDomain && lead.internal === true;
  const audDirty = [...aud].sort().join(',') !== [...(lead.audiences ?? [])].sort().join(',');
  const toggleAud = (l: LeadAudienceList) =>
    setAud((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));

  // Known-contact intelligence — the platform picture for this contact
  // (read-only aggregate; same permission gate as the page).
  const intelQ = useQuery({
    queryKey: ['admin-lead-intel', lead.id],
    queryFn: () => fetchLeadIntelligence(lead.id),
    staleTime: 60_000,
  });
  const intel = intelQ.data;

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await updateLead(lead.id, { notes });
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveNextAction = async () => {
    setSavingNext(true);
    setErr(null);
    try {
      const updated = await updateLead(lead.id, { next_action_at: nextAction || null });
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingNext(false);
    }
  };

  const markTouched = async () => {
    setTouching(true);
    setErr(null);
    try {
      const updated = await touchLead(lead.id);
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Touch failed');
    } finally {
      setTouching(false);
    }
  };

  const saveAudiences = async () => {
    setSavingAud(true);
    setErr(null);
    try {
      const updated = await updateLead(lead.id, { audiences: aud });
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingAud(false);
    }
  };

  const toggleInternal = async () => {
    setMarkingInternal(true);
    setErr(null);
    try {
      const updated = await updateLead(lead.id, { internal: !lead.internal });
      onSaved(updated);
    } catch (e) {
      setErr(await getDisplayErrorMessage(e, 'The internal mark did not change'));
    } finally {
      setMarkingInternal(false);
    }
  };

  const toggleSuppress = async () => {
    // Lifting a suppression mails the person again: confirm, and when it
    // records their unsubscribe, ask how they consented again (the Api
    // refuses it without that note).
    let note: string | undefined;
    if (lead.suppressed) {
      if (isUnsubscribeSuppressionReason(lead.suppression_reason)) {
        const typed = window.prompt(unsuppressReconsentPrompt(lead.suppression_reason ?? ''));
        if (typed === null) return;
        note = typed.trim();
      } else if (!window.confirm(unsuppressConfirmText(lead.suppression_reason ?? null))) {
        return;
      }
    }
    setSuppressing(true);
    setErr(null);
    try {
      const updated = lead.suppressed
        ? await unsuppressLead(lead.id, note)
        : await suppressLead(lead.id);
      onSaved(updated);
    } catch (e) {
      setErr(await getDisplayErrorMessage(e, 'Suppression change failed'));
    } finally {
      setSuppressing(false);
    }
  };

  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <aside className={styles.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Lead detail">
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>{lead.email}</h2>
          <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <dl className={styles.detailGrid}>
          <Field label="Name" value={lead.name ?? '—'} />
          <Field label="Phone" value={lead.phone ?? '—'} />
          <Field label="Audience" value={humanize(lead.audience ?? 'other')} />
          <Field label="Source" value={lead.source} mono />
          <Field label="Status" value={humanize(lead.status ?? 'new')} />
          <Field label="Tags" value={lead.tags.length ? lead.tags.join(', ') : '—'} />
          <Field label="Consent source" value={lead.consent_source ?? '—'} />
          <Field label="Consent at" value={fmtDate(lead.consent_at)} />
          <Field label="Created" value={fmtDate(lead.created_at)} />
          <Field label="Unsubscribed" value={lead.unsubscribed_at ? fmtDate(lead.unsubscribed_at) : '—'} />
        </dl>

        {/* Send-list memberships (slice 3): the five, multi-select. Chips toggle
            local state; Save persists the array. */}
        <div className={styles.notesBlock}>
          <span className={styles.notesLabel}>Audiences (send lists)</span>
          <div className={styles.chipRow}>
            {LEAD_AUDIENCE_LISTS.map((l) => (
              <button
                key={l}
                type="button"
                className={aud.includes(l) ? `${styles.toggleBtn} ${styles.toggleBtnActive}` : styles.toggleBtn}
                aria-pressed={aud.includes(l)}
                onClick={() => toggleAud(l)}
              >
                {LEAD_AUDIENCE_LIST_LABELS[l]}
              </button>
            ))}
          </div>
          <div className={styles.drawerActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={!audDirty || savingAud}
              onClick={saveAudiences}
            >
              {savingAud ? 'Saving…' : 'Save audiences'}
            </button>
          </div>
        </div>

        {/* Global suppression (slice 3): the org-wide do-not-send switch. A
            suppressed contact exits every send on every source/audience. */}
        <div className={styles.notesBlock}>
          <span className={styles.notesLabel}>
            Suppression {lead.suppressed && <span className={styles.suppressedBadge}>suppressed</span>}
          </span>
          <p className={styles.intelMuted}>
            {lead.suppressed
              ? 'This contact is globally suppressed — it receives no sends on any source or audience.'
              : 'Suppress to permanently stop all sends to this contact, across every source and audience.'}
          </p>
          {lead.suppressed && (
            <p className={styles.intelMuted}>Reason: {lead.suppression_reason || 'none recorded'}</p>
          )}
          <div className={styles.drawerActions}>
            <button
              type="button"
              className={lead.suppressed ? styles.secondaryBtn : styles.dangerBtn}
              disabled={suppressing}
              onClick={toggleSuppress}
            >
              {suppressing
                ? 'Working…'
                : lead.suppressed ? 'Un-suppress' : 'Suppress (do not send)'}
            </button>
          </div>
        </div>
        {/* Internal / test (Warren, 28 October 2026): an internal lead is never
            scored, never in the overdue digest or speed-to-lead, never sent a
            campaign. It holds for the address, every row at this email. */}
        <div className={styles.notesBlock}>
          <span className={styles.notesLabel}>
            Internal / test {lead.internal && <span className={styles.internalBadge}>Internal</span>}
          </span>
          <p className={styles.intelMuted}>
            {lead.internal
              ? 'Never scored, never in the overdue digest or speed-to-lead, never sent a campaign. Applies to every lead at this address.'
              : 'Mark a staff or test address so it is never scored, digested or sent a campaign. Applies to every lead at this address.'}
          </p>
          {(lead.internal || eviDomain) && (
            <p className={styles.intelMuted}>
              {INTERNAL_REASON_TEXT[eviDomain ? 'evi_domain' : lead.internal_reason ?? ''] ?? 'Marked internal.'}
            </p>
          )}
          <div className={styles.drawerActions}>
            <button
              type="button"
              className={lead.internal ? `${styles.toggleBtn} ${styles.toggleBtnActive}` : styles.toggleBtn}
              aria-pressed={lead.internal === true}
              disabled={markingInternal || internalLocked}
              onClick={toggleInternal}
            >
              {markingInternal ? 'Working…' : lead.internal ? 'Internal / test: on' : 'Internal / test: off'}
            </button>
          </div>
        </div>
        {/* Platform picture — the EVI edge: this contact across the whole
            platform, not just this row. Read-only. */}
        <h3 className={styles.drawerSectionTitle}>Platform picture</h3>
        {intelQ.isLoading && <p className={styles.intelMuted}>Loading contact intelligence…</p>}
        {intelQ.isError && <p className={styles.intelMuted}>Contact intelligence unavailable.</p>}
        {intel && (
          <div className={styles.intelBlock}>
            <dl className={styles.detailGrid}>
              <Field
                label="Score"
                value={`${intel.score.lead_score} · ${humanize(intel.score.band)} (R${intel.score.score_recency} F${intel.score.score_frequency} M${intel.score.score_monetary} I${intel.score.score_intent})`}
              />
              <Field
                label="Driver"
                value={intel.user
                  ? `${humanize(intel.user.role)} · ${intel.user.total_sessions} sessions · ${intel.user.total_kwh.toFixed(1)} kWh · ${intel.user.points} pts`
                  : 'No linked account'}
              />
              <Field
                label="Orders"
                value={intel.orders.count > 0
                  ? `${intel.orders.count} · $${(intel.orders.lifetime_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} lifetime`
                  : 'None'}
              />
              <Field
                label="Sales partner"
                value={intel.agent
                  ? `${intel.agent.deployed_paid_count} deployed${intel.agent.tier_boundary ? ` · tier ${intel.agent.tier_boundary}+` : ''}`
                  : '—'}
              />
            </dl>
            <ul className={styles.intelRows}>
              {intel.rows.map((r) => (
                <li key={`${r.source}`} className={styles.intelRow}>
                  <span className={styles.fieldValueMono}>{r.source}</span>
                  <span>{humanize(r.status)}</span>
                  <span className={styles.intelMuted}>{fmtDate(r.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Follow-up scheduling + touch (slice 2): an editable next-action
            date and a one-tap "mark touched" that records last_touched_at with
            this admin's id (audited) and drops the lead off the overdue digest. */}
        <div className={styles.notesBlock}>
          <label className={styles.notesLabel} htmlFor="lead-next-action">Next action date</label>
          <input
            id="lead-next-action"
            type="date"
            className={styles.notesInput}
            value={nextAction}
            onChange={(e) => setNextAction(e.target.value)}
          />
          <dl className={styles.detailGrid}>
            <Field label="Last touched" value={lead.last_touched_at ? fmtDate(lead.last_touched_at) : '—'} />
          </dl>
          <div className={styles.drawerActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={touching}
              onClick={markTouched}
            >
              {touching ? 'Marking…' : 'Mark touched'}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={!nextDirty || savingNext}
              onClick={saveNextAction}
            >
              {savingNext ? 'Saving…' : 'Save next action'}
            </button>
          </div>
        </div>

        <div className={styles.notesBlock}>
          <label className={styles.notesLabel} htmlFor="lead-notes">Notes</label>
          <textarea
            id="lead-notes"
            className={styles.notesInput}
            value={notes}
            maxLength={4000}
            rows={5}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes…"
          />
          {err && <div className={styles.errorBanner}>{err}</div>}
          <div className={styles.drawerActions}>
            <button type="button" className={styles.primaryBtn} disabled={!dirty || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.field}>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd className={mono ? styles.fieldValueMono : styles.fieldValue}>{value}</dd>
    </div>
  );
}
